import { Box, Button, MenuItem, Popover, Select } from '@mui/material'
import React from 'react'
import { allVanityUrlsByProductId } from '../../../api/vanityUrls.atoms'
import { useVanityUrlForTreeId } from '../../../api/vanityUrls.hooks'
import { useAtom, useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  useDeleteAll,
  useResetCrossAssociationTree,
  useUpdateAll,
  useUpdatesForNodeId,
} from './hooks'
import {
  allDeletesAtom,
  allUpdatesAtom,
  crossAssociationTreeAtom,
  doingUpdatesAtom,
  productAtom,
  productIdAtom,
} from './atoms'
import { goToNode } from '../../masters_management/utility'
import { doUpdate } from '../nodeUpdates/utility'
import { UpdateNodeData } from '../nodeUpdates/types'

export function LanguageSelect({
  selectedLanguage,
  onLanguageSelect,
}: {
  selectedLanguage: string
  onLanguageSelect: (l: string) => void
}) {
  return (
    <Select
      value={selectedLanguage}
      onChange={({ target: { value } }) => onLanguageSelect(value)}
    >
      <MenuItem value="English">English</MenuItem>
      <MenuItem value="Spanish">Spanish</MenuItem>
    </Select>
  )
}

export function VanityIdSelect() {
  const getVanityId = useVanityUrlForTreeId()
  const tree = useAtomValue(crossAssociationTreeAtom)

  if (!tree) return null

  const vanityData = getVanityId(tree.tree_id)

  if (vanityData) {
    return (
      <Box>
        <Select disabled={true} value={vanityData.vanityId}>
          <MenuItem value={vanityData.vanityId}>{vanityData.vanityId}</MenuItem>
        </Select>
        {vanityData?.productTitle}
      </Box>
    )
  }

  return <OpenVanitySelect />
}

function OpenVanitySelect() {
  const vanityUrls = useAtomValue(allVanityUrlsByProductId)
  const [productId, setProductId] = useAtom(productIdAtom)
  const product = useAtomValue(productAtom)

  return (
    <Box>
      <Select
        value={productId}
        onChange={(e) => {
          setProductId(parseInt(String(e.target.value), 10))
        }}
      >
        {Object.entries(vanityUrls).map(([pid, vanityUrl]) => {
          const { url } = vanityUrl
          return (
            <MenuItem key={pid} value={pid}>
              {url}
            </MenuItem>
          )
        })}
      </Select>
      {product?.title}
    </Box>
  )
}

export function GoToNode({ nodeId }: { nodeId: string }) {
  const tree = useAtomValue(crossAssociationTreeAtom)
  const go = () => {
    if (!tree) return
    const { tree_id, version } = tree
    goToNode({ tree_id, version, id: nodeId })
  }
  return <Button onClick={go}>Go</Button>
}

export function CrossRefUpdateButtons({
  mapping,
}: {
  mapping: [string, string]
}) {
  return (
    <Box>
      {mapping.map((nid) => {
        return (
          <Box key={nid}>
            <UpdateButton nodeId={nid} />
          </Box>
        )
      })}
    </Box>
  )
}

export function UpdateAllButton() {
  const doUpdateAll = useUpdateAll()
  const updating = useAtomValue(doingUpdatesAtom)
  const updates = useAtomValue(allUpdatesAtom)

  return (
    <Button disabled={updating || !updates.length} onClick={doUpdateAll}>
      Update All {updates.length}
    </Button>
  )
}

export function DeleteAllButton() {
  const deletes = useAtomValue(allDeletesAtom)
  const doDeleteAll = useDeleteAll()
  return (
    <Button onClick={doDeleteAll} disabled={!deletes.length}>
      Delete All {deletes.length}
    </Button>
  )
}

function UpdateButton({ nodeId }: { nodeId: string }) {
  const resetTree = useResetCrossAssociationTree()
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const [updating, setUpdating] = useAtom(doingUpdatesAtom)

  const nodeUpdates = useUpdatesForNodeId(nodeId)
  const updates = Object.values(nodeUpdates).reduce<UpdateNodeData[]>(
    (acc, u) => [...acc, ...u],
    [],
  )

  const doUpdatePress = async () => {
    setUpdating(true)
    await doUpdate(...updates)
    resetTree()
    setUpdating(false)
  }

  return (
    <Box
      onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
      onMouseLeave={() => setAnchorEl(null)}
    >
      <Button disabled={updating || !updates.length} onClick={doUpdatePress}>
        Update {updates.length}
      </Button>
      <Popover
        open={!!anchorEl && !!updates.length}
        sx={{ pointerEvents: 'none' }}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={() => setAnchorEl(null)}
        disableRestoreFocus
      >
        <Box padding="5px">
          {Object.entries(nodeUpdates).map(([label, list]) => {
            return <Box key={label}>{`${label}: ${list.length}`}</Box>
          })}
        </Box>
      </Popover>
    </Box>
  )
}
